import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import PageTitle from "../../components/common/pages/PageTitle";
import { observer } from "mobx-react";
import { FilterValue } from "../../../models/Filters/FilterValue";
import AppDropDown from "../../components/common/filter/AppDropDown";
import { CouponFilters } from "../../../models/inventory/CouponsFilters";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { SortType } from "../../../models/SortType";
import CouponsTable from "../../components/Inventory/CouponsTable";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import OnelinkRequestsTable from "../../components/order/OnelinkRequestsTable";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";

interface OnelinkRequestsSelectedFilters {
  order_id: FilterValue[];
  customer_id: FilterValue[];
  status: string;
  active: boolean;
}

const OnelinkRequestsListing: React.FC = () => {
  const { onelinkRequestStore, customerStore, orderStore, userStore } =
    useStores();

  const { showError, showWarning, showSuccess } = useNotification();

  const [selectedShipmentIdsMap, setSelectedShipmentIdsMap] = useState<
    Map<number, number>
  >(new Map<number, number>());

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialFilters: OnelinkRequestsSelectedFilters = {
    order_id: searchParams.get("order_id")
      ? searchParams
          .get("order_id")!
          .split(",")
          .map((id) => ({ id: id, value: id }))
      : [],
    customer_id: searchParams.get("customer_id")
      ? searchParams
          .get("customer_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":")!;
            return { id: list[0], value: list[1] };
          })
      : [],
    status: searchParams.get("status") ? searchParams.get("status")! : "0",
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
  };

  const initialParams: CouponFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    order_id: initialFilters.order_id.map((item) => Number(item.id)),
    customer_id: initialFilters.customer_id.map((item) => Number(item.id)),
    status: searchParams.get("status") ? Number(searchParams.get("status")) : 0,
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const [filters, setFilters] =
    useState<OnelinkRequestsSelectedFilters>(initialFilters);
  const [params, setParams] = useState(initialParams);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [isLoading, setIsLoading] = useState(false);

  // Confirmation popup state
  const [confirmationPopup, setConfirmationPopup] = useState({
    open: false,
    id: 0,
    order_id: 0,
    action: "" as "approve" | "reject" | "",
    title: "",
    content: "",
  });

  let apiTimer: any;

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_ONELINK_REQUESTS)) {
      setIsLoading(true);
      onelinkRequestStore
        .getOnelinkRequests(params)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          if (key === "customer_id") {
            const paramValue = filters.customer_id
              .map((item) => item.id + ":" + item.value)
              .join(",");
            searchParams.set(key, paramValue);
          } else {
            const paramValue = Array.isArray(value)
              ? value.join(",")
              : value.toString();
            searchParams.set(key, paramValue);
          }
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleSearchBar = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.length !== 0) {
      setSearchTerm(e.target.value);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handleApply = () => {
    setSelectedShipmentIdsMap(new Map());
    setParams((prev) => ({
      ...prev,
      order_id: filters.order_id.map((item) => Number(item.id)),
      customer_id: filters.customer_id.map((item) => Number(item.id)),
      status: Number(filters.status),
      active: filters.active,
    }));
  };

  const resetFilters = () => {
    setSelectedShipmentIdsMap(new Map());
    setSearchTerm("");
    setFilters({
      order_id: [],
      customer_id: [],
      status: "0",
      active: true,
    });
    setParams({
      page_no: 1,
      limit: 30,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      order_id: [],
      customer_id: [],
      status: 0,
      active: true,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {});
      }, 1500);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  // Confirmation popup handlers
  const handleApproveRequest = (id: number, order_id: number) => {
    setConfirmationPopup({
      open: true,
      id,
      order_id,
      action: "approve",
      title: "Approve OneLink Request",
      content: `Are you sure you want to approve this OneLink request for Order ID: ${order_id}?`,
    });
  };

  const handleRejectRequest = (id: number, order_id: number) => {
    setConfirmationPopup({
      open: true,
      id,
      order_id,
      action: "reject",
      title: "Reject OneLink Request",
      content: `Are you sure you want to reject this OneLink request for Order ID: ${order_id}?`,
    });
  };

  const handleConfirmationConfirm = (id: number) => {
    const { action, order_id } = confirmationPopup;

    if (action === "approve") {
      onelinkRequestStore
        .approveOnelinkRequest(id, order_id)
        .then(() => {
          showSuccess("OneLink request approved successfully");
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message || "Failed to approve request"
          );
        });
    } else if (action === "reject") {
      onelinkRequestStore
        .rejectOnelinkRequest(id, order_id)
        .then(() => {
          showSuccess("OneLink request rejected successfully");
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message || "Failed to reject request"
          );
        });
    }

    setConfirmationPopup({
      open: false,
      id: 0,
      order_id: 0,
      action: "",
      title: "",
      content: "",
    });
  };

  const handleConfirmationCancel = () => {
    setConfirmationPopup({
      open: false,
      id: 0,
      order_id: 0,
      action: "",
      title: "",
      content: "",
    });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ maxHeight: window.innerHeight - 180 }}>
        <PageTitle title="OneLink Requests" />
        {!userStore.hasPermission(EmployeeRoles.VIEW_ONELINK_REQUESTS) && (
          <PermissionBlockLabel />
        )}
        {userStore.hasPermission(EmployeeRoles.VIEW_ONELINK_REQUESTS) && (
          <Box>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  paddingBottom: 3,
                  justifyContent: "right",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <TextField
                    id="standard-search"
                    placeholder="Email"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => handleSearchBar(e)}
                    onKeyDown={(e) => handleKeyDown(e)}
                    size="medium"
                    sx={{
                      minWidth: "200px",
                      "& .MuiOutlinedInput-input": {
                        padding: "6px 10px",
                        fontSize: "12px",
                      },
                    }}
                  />
                  <Button
                    sx={{
                      minWidth: "80px",
                      padding: "6px 10px",
                      fontSize: "12px",
                    }}
                    variant="contained"
                    onClick={handleSearchSubmit}
                  >
                    Search
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  alignItems: "center",
                  justifyContent: "end",
                  display: "flex",
                  gap: 2,
                  paddingBottom: 2,
                  maxHeight: 40,
                }}
              >
                <Box
                  sx={{
                    width: 200,
                  }}
                >
                  <AppSearchDropDown
                    isMultiSelect={true}
                    title="Select Order Id"
                    value={filters.order_id}
                    fontSize="12px"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 200,
                  }}
                >
                  <AppSearchDropDown
                    isMultiSelect={true}
                    title="Select Customers"
                    value={filters.customer_id}
                    fontSize="12px"
                    list={customerStore.customers.map((item) => {
                      return { id: String(item.id), value: item.name! };
                    })}
                    local={false}
                    handleSearch={handleCustomerSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        customer_id: items,
                      }));
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 200,
                  }}
                >
                  <AppDropDown
                    title="Status"
                    isMultiSelect={false}
                    value={filters.status}
                    list={[
                      { id: "0", value: "Select" },
                      { id: "1", value: "Pending" },
                      { id: "2", value: "Approved" },
                      { id: "3", value: "Rejected" },
                    ]}
                    fontSize={"12px"}
                    onSingleSelectionChange={(id) => {
                      setFilters((prev) => ({
                        ...prev,
                        status: id,
                      }));
                    }}
                  />
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      maxHeight: "34px",
                      padding: "6px 16px",
                      fontSize: "12px",
                    }}
                    onClick={handleApply}
                  >
                    Apply
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      maxHeight: "34px",
                      padding: "6px 16px",
                      fontSize: "12px",
                    }}
                    onClick={resetFilters}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Box>
            {isLoading && (
              <Container
                sx={{
                  minHeight: "60vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Container>
            )}

            {!isLoading && (
              <Box>
                {onelinkRequestStore.onelinkRequests.length === 0 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="10rem"
                    textAlign="center"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      No data found
                    </Typography>
                  </Box>
                )}
                {onelinkRequestStore.onelinkRequests.length > 0 && (
                  <OnelinkRequestsTable
                    maxHeight={window.innerHeight - 350}
                    onelinkRequests={onelinkRequestStore.onelinkRequests}
                    limit={params.limit || onelinkRequestStore.limit}
                    page_no={params.page_no}
                    total_count={onelinkRequestStore.total_count}
                    total_pages={onelinkRequestStore.total_pages}
                    sortBy={params.sort_by}
                    sortType={params.sort_type}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onSortChange={(column, sortType) => {
                      setParams((prev) => ({
                        ...prev,
                        sort_by: column,
                        sort_type: sortType,
                      }));
                    }}
                    selectedShipmentIdsMap={selectedShipmentIdsMap}
                    selectAllItem={() => {
                      const map = new Map<number, number>();
                      onelinkRequestStore.onelinkRequests.forEach((item) =>
                        map.set(item.id, item.id)
                      );
                      setSelectedShipmentIdsMap(map);
                    }}
                    deSelectAllItem={() => {
                      setSelectedShipmentIdsMap(new Map());
                    }}
                    selectItem={(item) => {
                      const map = new Map(selectedShipmentIdsMap);
                      map.set(item.id, item.id);
                      setSelectedShipmentIdsMap(map);
                    }}
                    deSelectItem={(item) => {
                      const map = new Map(selectedShipmentIdsMap);
                      map.delete(item.id);
                      setSelectedShipmentIdsMap(map);
                    }}
                    canApprove={userStore.hasPermission(
                      EmployeeRoles.APPROVE_REJECT_ONELINK_REQUEST
                    )}
                    approveOnelinkRequest={handleApproveRequest}
                    rejectOnelinkRequest={handleRejectRequest}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Confirmation Popup */}
      <ConfirmationPopup
        id={confirmationPopup.id}
        open={confirmationPopup.open}
        title={confirmationPopup.title}
        content={confirmationPopup.content}
        onConfirm={handleConfirmationConfirm}
        onCancel={handleConfirmationCancel}
      />
    </Container>
  );
};

export default observer(OnelinkRequestsListing);
